import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMutuallyExclusiveAccordions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    if (details === this.refs.details) {
      this.classList.remove('header-drawer--summary-close-icon');
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (details === this.refs.details) {
        const drawer = details.querySelector(':scope > .menu-drawer');
        const delaySummaryCloseIcon = window.matchMedia('(max-width: 749px)').matches;

        if (delaySummaryCloseIcon && drawer instanceof HTMLElement) {
          let done = false;
          const showCloseIcon = () => {
            if (done || !details.classList.contains('menu-open')) return;
            done = true;
            this.classList.add('header-drawer--summary-close-icon');
          };

          /** @param {TransitionEvent} e */
          const onEnd = (e) => {
            if (e.target !== drawer || e.propertyName !== 'transform') return;
            showCloseIcon();
          };
          drawer.addEventListener('transitionend', onEnd);
          window.setTimeout(() => {
            drawer.removeEventListener('transitionend', onEnd);
            showCloseIcon();
          }, 400);
        } else if (delaySummaryCloseIcon) {
          this.classList.add('header-drawer--summary-close-icon');
        }
      }

      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.classList.remove('header-drawer--summary-close-icon');
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        // Close nested drill-in panels and accordions so reopening the drawer starts fresh.
        // Accordions are `accordion-custom > details` and were wrongly excluded by :not(accordion-custom > details).
        this.querySelectorAll('.menu-drawer details[open]').forEach((el) => {
          if (el instanceof HTMLDetailsElement) reset(el);
        });
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Sets up mutually exclusive accordions within submenus.
   * When one accordion opens, all other accordions in the same submenu close.
   */
  #setupMutuallyExclusiveAccordions() {
    // Find all submenu containers
    const submenus = this.querySelectorAll('.menu-drawer__submenu');

    submenus.forEach((submenu) => {
      // Find all accordions within this submenu
      const accordions = submenu.querySelectorAll('accordion-custom.menu-drawer__submenu-accordion');

      accordions.forEach((accordion) => {
        const details = accordion.querySelector('details');
        if (!details) return;

        // Listen for toggle events on each accordion
        details.addEventListener('toggle', () => {
          if (details.open) {
            // When this accordion opens, close all other accordions in the same submenu
            accordions.forEach((otherAccordion) => {
              if (otherAccordion !== accordion) {
                const otherDetails = otherAccordion.querySelector('details');
                if (otherDetails && otherDetails.open) {
                  otherDetails.open = false;
                }
              }
            });
          }
        });
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
